import { supabase } from "@/integrations/supabase/client";
import {
  getAllQueued,
  removeAction,
  updateAction,
  type QueuedAction,
} from "./offlineQueue";

const MAX_RETRIES = 5;

/** Exponential backoff schedule (minutes) per retry attempt 1..MAX_RETRIES. */
const BACKOFF_MINUTES = [1, 2, 5, 15, 30];

function nextRetryDelayMs(retries: number): number {
  const idx = Math.min(retries, BACKOFF_MINUTES.length - 1);
  return BACKOFF_MINUTES[idx] * 60_000;
}

/**
 * Process every queued action sequentially.
 * Returns counts for synced, failed, deduped, and skipped (still in backoff).
 */
export async function processQueue(): Promise<{
  synced: number;
  failed: number;
  deduped: number;
  skipped: number;
}> {
  const items = await getAllQueued();
  if (items.length === 0)
    return { synced: 0, failed: 0, deduped: 0, skipped: 0 };

  // Sort oldest first
  items.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  // Collapse duplicate tuition scans for the same (student, group, date)
  // into the earliest queued record. Same logic applies to gate/session scans.
  const deduped = await dedupeAttendanceInserts(items);

  let synced = 0;
  let failed = 0;
  let skipped = 0;
  const now = Date.now();

  for (const item of items) {
    // Respect backoff and manual-resolution gating
    if (item.needsManualResolution) {
      skipped++;
      continue;
    }
    if (item.nextRetryAt && new Date(item.nextRetryAt).getTime() > now) {
      skipped++;
      continue;
    }

    try {
      await executeAction(item);
      await removeAction(item.id);
      synced++;
    } catch (err: any) {
      item.retries++;
      item.lastError = err?.message ?? "Unknown error";
      if (item.retries >= MAX_RETRIES) {
        // Stop auto-retrying; surface to leader for manual resolution
        item.needsManualResolution = true;
        item.nextRetryAt = undefined;
        await updateAction(item);
      } else {
        item.nextRetryAt = new Date(
          Date.now() + nextRetryDelayMs(item.retries - 1)
        ).toISOString();
        await updateAction(item);
      }
      failed++;
    }
  }

  return { synced, failed, deduped, skipped };
}

/**
 * Collapse duplicate attendance inserts queued offline.
 *
 * When a leader scans the same student multiple times during one placement
 * day (e.g. retries after a flaky network), we want only ONE row to land in
 * `attendance_records`. We key by attendance_type + student_id +
 * field_group_id (or session_id) + attendance_date and keep the earliest
 * queued entry, removing the rest.
 *
 * Mutates the input array (removes collapsed items) and returns the count
 * of records that were dropped.
 */
async function dedupeAttendanceInserts(items: QueuedAction[]): Promise<number> {
  const seen = new Map<string, QueuedAction>();
  const toDrop: QueuedAction[] = [];

  for (const item of items) {
    if (item.type !== "insert") continue;
    const p = item.payload as any;
    if (p?.table !== "attendance_records") continue;
    const data = p?.data ?? {};
    const studentId = data.student_id;
    if (!studentId) continue;

    const date =
      data.attendance_date ?? new Date(item.createdAt).toISOString().slice(0, 10);
    const windowKey =
      data.field_group_id ?? data.session_id ?? data.attendance_type ?? "gate";
    const key = `${data.attendance_type ?? "session"}|${studentId}|${windowKey}|${date}`;

    const prior = seen.get(key);
    if (!prior) {
      seen.set(key, item);
    } else {
      // Keep the earliest (already in `seen`); drop this newer duplicate.
      toDrop.push(item);
    }
  }

  for (const item of toDrop) {
    await removeAction(item.id);
    const idx = items.indexOf(item);
    if (idx >= 0) items.splice(idx, 1);
  }

  if (toDrop.length > 0) {
    window.dispatchEvent(new CustomEvent("offline-queue-changed"));
  }

  return toDrop.length;
}

async function executeAction(action: QueuedAction): Promise<void> {
  const { type, payload } = action;

  switch (type) {
    case "approval": {
      const { id, status, review_notes, reviewed_by } = payload as any;
      const { error } = await supabase
        .from("approvals")
        .update({
          status,
          review_notes: review_notes ?? null,
          reviewed_by,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", id);
      if (error) throw error;
      break;
    }

    case "insert": {
      const { table, data } = payload as any;
      const { error } = await (supabase.from as any)(table).insert(data);
      if (error) throw error;
      break;
    }

    case "update": {
      const { table, data, matchColumn, matchValue } = payload as any;
      const { error } = await (supabase.from as any)(table)
        .update(data)
        .eq(matchColumn, matchValue);
      if (error) throw error;
      break;
    }

    case "rpc": {
      const { fn, args } = payload as any;
      const { error } = await (supabase.rpc as any)(fn, args);
      if (error) throw error;
      break;
    }

    case "upload": {
      // Uploads store the file as a base64 data URL
      const { bucket, path, base64, contentType } = payload as any;
      const binary = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
      const { error } = await supabase.storage
        .from(bucket)
        .upload(path, binary, { contentType, upsert: true });
      if (error) throw error;
      break;
    }

    default:
      throw new Error(`Unknown queued action type: ${type}`);
  }
}
